export interface ProblemJson {
  type: string
  title: string
  status: number
  code: string
  detail?: unknown
}

export class ApiError extends Error {
  constructor(readonly problem: ProblemJson) {
    super(problem.title)
    this.name = 'ApiError'
  }
  get code(): string {
    return this.problem.code
  }
  /** Nombre que hay que tipear cuando la instancia está protegida. */
  get expectedName(): string | null {
    const d = this.problem.detail as { expectedName?: string } | undefined
    return d?.expectedName ?? null
  }
}

const BASE = '/api/v1'

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'same-origin',
    headers: body !== undefined ? { 'content-type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const data = text ? JSON.parse(text) : null

  if (!res.ok) {
    throw new ApiError(
      data ?? { type: '', title: `Error ${res.status}`, status: res.status, code: 'unknown' },
    )
  }
  return data as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body ?? {}),
}

// ── tipos que devuelve la API ──

export interface Me {
  user: { id: string; email: string; name: string; isSuperAdmin: boolean }
  isSuperAdmin: boolean
  globalCapabilities: string[]
}

export type DeploymentStatus =
  | 'STOPPED' | 'STARTING' | 'RUNNING' | 'STOPPING'
  | 'CRASHED' | 'DEPLOYING' | 'UPDATING' | 'UNHEALTHY'

export interface Deployment {
  id: string
  name: string
  environment: 'PRODUCTION' | 'STAGING' | 'DEVELOPMENT' | 'PREVIEW'
  status: DeploymentStatus
  statusMessage: string | null
  runtimeDriver: 'DOCKER' | 'COMPOSE' | 'SYSTEMD'
  protected: boolean
  adopted: boolean
  maintenanceMode: boolean
  port: number | null
  branch: string
  workDir: string
  systemdUnit: string | null
  composePath: string | null
  startCmd: string[]
  memoryLimitMb: number | null
  cpuLimit: number | null
  lastDeployedAt: string | null
  app: { id: string; name: string; slug: string; tags?: string[]; repoUrl?: string | null }
  server: { id: string; name: string; hostname?: string; proxyDriver?: string; proxyManaged?: boolean }
  domains: Array<{ id?: string; hostname: string; pathPrefix: string }>
  capabilities?: string[]
}

export interface AppRow {
  id: string
  name: string
  slug: string
  description: string | null
  tags: string[]
  deployments: Array<{ id: string; name: string; environment: string; status: DeploymentStatus; protected: boolean; adopted: boolean }>
}

export interface ServerRow {
  id: string
  name: string
  hostname: string
  publicIp: string | null
  status: 'PENDING' | 'ONLINE' | 'OFFLINE' | 'DEGRADED'
  isLocal: boolean
  proxyDriver: 'NONE' | 'CADDY' | 'NGINX'
  proxyManaged: boolean
  _count?: { deployments: number }
  proxy?: { available: boolean; driver: string; detail: string; managed: boolean }
}

export interface EnvRow {
  key: string
  value: string
  isSecret: boolean
  masked: boolean
  updatedAt: string
}

export interface AuditRow {
  id: string
  at: string
  action: string
  targetLabel: string
  result: 'SUCCESS' | 'FAILURE' | 'DENIED'
  actor: string
  ip: string
  viaMcp: boolean
}

export interface RuntimeStatusLive {
  status: DeploymentStatus
  detail: string
  pid?: number
  cpuPercent?: number
  memoryBytes?: number
  startedAt?: string | null
}

export interface PermissionPreview {
  access: Array<{ target: string; capabilities: string[]; protected: boolean }>
  globalCapabilities: string[]
  noAccessTo: string[]
  warnings: string[]
}

// ── Fase 3 ──

export interface FileEntry {
  name: string
  path: string
  isDirectory: boolean
  isSymlink: boolean
  escapes: boolean
  size: number
  mode: string
  modifiedAt: string
}

export interface FileListing {
  path: string
  parent: string | null
  entries: FileEntry[]
}

export interface FileContent {
  path: string
  content: string
  size: number
  tooLarge: boolean
  binary: boolean
  head: string | null
}

export interface GitStatus {
  isRepo: boolean
  branch: string | null
  ahead: number
  behind: number
  clean: boolean
  files: Array<{ path: string; code: string; label: string; staged: boolean }>
  lastCommit: { sha: string; message: string; author: string; date: string } | null
}

export interface DatabaseRow {
  id: string
  label: string
  engine: 'POSTGRES' | 'MYSQL' | 'MARIADB' | 'REDIS' | 'SQLITE'
  host: string
  port: number
  name: string
  user: string
  readOnlyMode: boolean
  provisionedByPanel: boolean
  deploymentId: string
  deployment?: { id: string; name: string; protected: boolean; app: { slug: string } }
}

export interface QueryResult {
  columns: Array<{ name: string; type: string }>
  rows: Array<Record<string, unknown>>
  rowCount: number
  affectedRows?: number
  durationMs: number
  truncated: boolean
  analysis?: { readOnly: boolean; kinds: string[] }
}

export interface RiskReport {
  readOnly: boolean
  dangers: string[]
  multiStatement: boolean
  statements: Array<{ kind: string; tables: string[]; dangers: string[] }>
  requiresConfirmation: boolean
  confirmName: string | null
  blockedReason: string | null
}

export interface TableRow {
  schema: string
  name: string
  rowEstimate: number | null
  sizeBytes: number | null
}

export interface BackupRow {
  id: string
  kind: 'FILES' | 'DATABASE' | 'FULL'
  destination: 'LOCAL' | 'S3' | 'SFTP'
  location: string
  sizeBytes: number | null
  checksum: string | null
  status: 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED' | 'VERIFIED'
  trigger: string
  error: string | null
  retainUntil: string | null
  createdAt: string
  deploymentId: string
  deployment?: { name: string; protected: boolean; app: { slug: string } }
}
